import { CHAT_POLL_OPEN_MS, useChatMessages } from '@/api/hooks/useChatMessages'
import { useMarkChatMessagesConsumed } from '@/api/hooks/useMarkChatMessagesConsumed'
import { type OutgoingMessage, useSendChatMessage } from '@/api/hooks/useSendChatMessage'
import type { ChatMessage, LocalAttachment } from '@/api/types/MessagingApi'
import { useNavBarHeight } from '@/components/AppTabBar/height'
import { MAX_ATTACHMENTS } from '@/components/SupportPage/AttachmentPicker'
import { ChatComposer } from '@/components/SupportPage/ChatComposer'
import { ChatThread } from '@/components/SupportPage/ChatThread'
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_NUMBER } from '@/constants/support'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { PageShell } from '@/ui/core/page-shell'
import { Skeleton } from '@/ui/core/skeleton'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useIsFocused } from '@react-navigation/native'
import * as Linking from 'expo-linking'
import { useMemo, useState } from 'react'
import { Platform, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Toast from 'react-native-toast-message'
import Images from '#/assets/images/image-map'

/** The ways a conversation usually starts. Each drops a first line into the composer. */
const OPENERS: { label: string; draft: string }[] = [
	{ label: 'Reschedule', draft: "I'd like to move my next visit. " },
	{ label: 'My results', draft: 'I have a question about my results. ' },
	{ label: 'Billing', draft: 'I have a question about my billing. ' },
]

const byCreatedAt = (a: ChatMessage, b: ChatMessage) =>
	a.createdAt.localeCompare(b.createdAt)

/** Text-us support, for a server without the chat or a device that cannot reach it. */
const TextUs = ({ reason }: { reason: string }) => {
	const canText = Platform.OS === 'ios' || Platform.OS === 'android'
	const textUs = () => {
		analytics.trackClick('support-text-button-pressed')
		Linking.openURL(`sms:${SUPPORT_PHONE_NUMBER}`)
	}

	return (
		<PageShell photo={Images.BrandPhoto}>
			<VStack gap={32}>
				<VStack gap={16}>
					<Header variant="h2">We're here to help</Header>
					<Text className="max-w-[28rem]">{reason}</Text>
					<Text className="max-w-[28rem]">
						Text your Care Team at {SUPPORT_PHONE_DISPLAY} with anything at all, from
						rescheduling to a question about your results.
					</Text>
				</VStack>
				{canText && (
					<Button fullWidth={false} onPress={textUs}>
						<ButtonText>Text Us</ButtonText>
					</Button>
				)}
			</VStack>
		</PageShell>
	)
}

/** The first thing on an empty thread: an invitation, and three ways to begin. */
const Opener = ({ onPick }: { onPick: (draft: string) => void }) => (
	<View className="rounded-note bg-white px-5 py-5">
		<VStack gap={16}>
			<VStack gap={4}>
				<Text weight="bold" className="text-[20px] leading-[24px]">
					Say hello
				</Text>
				<Text size="body1" tone="soft">
					Your Care Team reads every message. Start with one of these, or anything at all.
				</Text>
			</VStack>
			<HStack gap={8} className="flex-wrap">
				{OPENERS.map((opener) => (
					<Button
						key={opener.label}
						variant="secondary"
						size="sm"
						fullWidth={false}
						onPress={() => onPick(opener.draft)}
					>
						<ButtonText>{opener.label}</ButtonText>
					</Button>
				))}
			</HStack>
		</VStack>
	</View>
)

/** Three bubbles' worth of shimmer while the thread loads. */
const ThreadSkeleton = () => (
	<VStack gap={12} className="px-4 pt-4">
		<Skeleton isLoading className="h-12 w-3/5 self-start rounded-note bg-white" />
		<Skeleton isLoading className="h-12 w-2/5 self-end rounded-note bg-ground" />
		<Skeleton isLoading className="h-16 w-4/6 self-start rounded-note bg-white" />
	</VStack>
)

/**
 * The support thread on a blush card: Wally's replies on the left in white, the patient's on the
 * right in ink, the composer along the card's foot. Polled while open.
 */
export const SupportThread = ({ draft: initialDraft }: { draft?: string }) => {
	const isFocused = useIsFocused()
	const { messages, isLoading, isError, isUnavailable, refetch } =
		useChatMessages(CHAT_POLL_OPEN_MS)
	const { mutate: send, failed, retry } = useSendChatMessage()
	const [draft, setDraft] = useState(initialDraft ?? '')
	const [composerHeight, setComposerHeight] = useState(76)
	const [files, setFiles] = useState<LocalAttachment[]>([])

	const addFiles = (picked: LocalAttachment[]) => {
		const room = MAX_ATTACHMENTS - files.length
		if (picked.length > room) {
			Toast.show({ type: 'error', text1: `Up to ${MAX_ATTACHMENTS} files per message` })
		}
		setFiles([...files, ...picked.slice(0, Math.max(0, room))])
	}
	const insets = useSafeAreaInsets()
	const navHeight = useNavBarHeight((s) => s.height)
	useMarkChatMessagesConsumed(messages, isFocused && !isError)

	const thread = useMemo(
		() => (failed.length ? [...messages, ...failed].sort(byCreatedAt) : messages),
		[messages, failed]
	)

	const onSend = (message: OutgoingMessage) => {
		analytics.trackEvent('support-chat-message-sent', {
			attachments: message.attachments.length,
		})
		send(message)
	}

	if (isUnavailable) {
		return <TextUs reason="Chat isn't switched on for your account yet." />
	}
	if (isError) {
		return (
			<PageShell photo={Images.BrandPhoto}>
				<VStack gap={24}>
					<VStack gap={12}>
						<Header variant="h2">We couldn't open your messages</Header>
						<Text>Check your connection and try again, or text us instead.</Text>
					</VStack>
					<HStack gap={12}>
						<Button
							variant="secondary"
							size="sm"
							fullWidth={false}
							onPress={() => refetch()}
						>
							<ButtonText>Retry</ButtonText>
						</Button>
						<Button
							variant="link"
							size="sm"
							onPress={() => Linking.openURL(`sms:${SUPPORT_PHONE_NUMBER}`)}
						>
							<ButtonText>Text {SUPPORT_PHONE_DISPLAY}</ButtonText>
						</Button>
					</HStack>
				</VStack>
			</PageShell>
		)
	}
	return (
		<View
			className="mx-5 flex-1 overflow-hidden rounded-note bg-blush lg:w-full lg:max-w-[780px] lg:self-center"
			// The card starts under the navigation with its own rounded top, rather than running
			// up beneath the glass.
			style={{ marginTop: navHeight, marginBottom: Math.max(insets.bottom, 12) }}
		>
			{isLoading ? (
				<ThreadSkeleton />
			) : (
				<ChatThread
					messages={thread}
					onRetry={retry}
					header={thread.length === 0 ? <Opener onPick={setDraft} /> : undefined}
					bottomInset={composerHeight}
				/>
			)}
			<ChatComposer
				draft={draft}
				onChangeDraft={setDraft}
				files={files}
				onChangeFiles={setFiles}
				onPicked={addFiles}
				onSend={onSend}
				onHeight={setComposerHeight}
			/>
		</View>
	)
}
